// src/components/BestSeller.jsx
import { useEffect, useState } from "react";
import { ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function BestSeller() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/best-sellers/")
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="py-24 bg-gray-50 font-['Roboto']">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-medium text-gray-900 mb-4">
            Best Sellers
          </h2>
          <p className="text-lg text-gray-600">
            Most loved by our customers
          </p>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 text-xl py-20">Loading...</p>
        ) : products.length === 0 ? (
          <p className="text-center text-gray-500 text-xl py-20">
            No best sellers yet
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((p) => {
              const first = p.variants?.[0];
              const totalStock = p.variants?.reduce((s, v) => s + v.stock, 0) || 0;
              const discount = p.offer_price 
                ? Math.round(((p.price - p.offer_price) / p.price) * 100)
                : 0;

              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/product/${p.id}`)}
                  className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100"
                >
                  {/* Image Container */}
                  <div className="relative h-96 bg-gray-50 overflow-hidden">
                    {first?.image_url ? (
                      <img
                        src={first.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No Image
                      </div>
                    )}

                    {/* OFFER Badge */}
                    {discount > 0 && (
                      <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                        -{discount}% OFF
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-medium text-gray-900 line-clamp-2 mb-3">
                      {p.name}
                    </h3>

                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl font-bold text-gray-900">
                        ₹{p.offer_price || p.price}
                      </span>
                      {p.offer_price && (
                        <span className="text-lg text-gray-500 line-through">
                          ₹{p.price}
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <strong>{p.variants?.length || 0}</strong> variant{p.variants?.length !== 1 && "s"}
                      </p>
                      <p className={totalStock < 10 ? "text-orange-600 font-medium" : "text-green-700"}>
                        {totalStock > 0 ? `${totalStock} in stock` : "Out of stock"}
                      </p>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/product/${p.id}`);
                      }}
                      className="mt-6 w-full py-4 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition flex items-center justify-center gap-3"
                    >
                      <ShoppingBag className="w-5 h-5" />
                      View Product
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}